// Port of Flutter's homeScreen.dart — the four-tab shell.
//
// Tabs, icons, labels and order are exactly the Dart:
//   Favourites (star) · Locations (pin) · Scenes (group work) · Me (person)
// The bar sits on BackColor with FontColor for the selected item and FontColor
// @30% for the rest, 14/12px labels, labels always shown, fixed type.
//
// The tab index is a route param (?tab=N) so the hardware back button and the
// "go to Locations" redirects behave like the Flutter pushReplacement calls.
//
// Beyond Flutter: the four tabs also respond to a horizontal SWIPE (left = next,
// right = previous, no wrap at the ends), with a short slide-in so the direction
// reads. Swipes that start inside a horizontal scroller, or that are mostly
// vertical, are left alone — see useSwipeNav.

import React, { useRef } from 'react';
import {
  Box, BottomNavigation, BottomNavigationAction, useTheme,
} from '@mui/material';
import StarRateRoundedIcon from '@mui/icons-material/StarRateRounded';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import GroupWorkIcon from '@mui/icons-material/GroupWork';
import PersonIcon from '@mui/icons-material/Person';
import { useSearchParams } from 'react-router-dom';

import FavouritesTab from './tabs/FavouritesTab';
import LocationsTab from './tabs/LocationsTab';
import ScenesTab from './tabs/ScenesTab';
import MeTab from './tabs/MeTab';
import useSwipeNav from '../components/useSwipeNav';

const TABS = [FavouritesTab, LocationsTab, ScenesTab, MeTab];

export default function MobileHomeScreen() {
  const theme = useTheme();
  const c = theme.sli;
  const [params, setParams] = useSearchParams();
  const index = Math.min(3, Math.max(0, Number(params.get('tab') || 0)));
  const Current = TABS[index];
  // Which way the last change went, so the incoming tab slides in from the side
  // the finger came from.
  const dirRef = useRef(1);

  const goTab = (i) => {
    dirRef.current = i > index ? 1 : -1;
    setParams({ tab: String(i) }, { replace: true });
  };

  const swipe = useSwipeNav(index, TABS.length, goTab);

  return (
    <Box sx={{
      height: '100%', display: 'flex', flexDirection: 'column', bgcolor: c.back,
    }}
    >
      <Box
        {...swipe}
        sx={{
          flex: 1,
          minHeight: 0,
          position: 'relative',
          // Let the browser own vertical scrolling; we only ever act on
          // horizontal gestures, so this keeps list scrolling perfectly smooth.
          touchAction: 'pan-y',
        }}
      >
        <Box
          // Remount per tab so the slide-in replays on every change.
          key={index}
          sx={{
            height: '100%',
            animation: `${dirRef.current > 0 ? 'sliRight' : 'sliLeft'} 180ms ease-out`,
            '@keyframes sliRight': {
              from: { opacity: 0, transform: 'translateX(18px)' },
              to: { opacity: 1, transform: 'translateX(0)' },
            },
            '@keyframes sliLeft': {
              from: { opacity: 0, transform: 'translateX(-18px)' },
              to: { opacity: 1, transform: 'translateX(0)' },
            },
          }}
        >
          <Current goTab={goTab} />
        </Box>
      </Box>

      <BottomNavigation
        value={index}
        onChange={(_, i) => goTab(i)}
        showLabels
        sx={{
          bgcolor: c.back,
          borderTop: `1px solid ${c.text}22`,
          '& .MuiBottomNavigationAction-root': {
            color: `${c.text}4D`,
            minWidth: 0,
          },
          '& .Mui-selected': { color: c.text },
          '& .MuiBottomNavigationAction-label': { fontSize: 12 },
          '& .Mui-selected .MuiBottomNavigationAction-label': { fontSize: 14 },
        }}
      >
        <BottomNavigationAction label="Favourites" icon={<StarRateRoundedIcon />} />
        <BottomNavigationAction label="Locations" icon={<LocationOnIcon />} />
        <BottomNavigationAction label="Scenes" icon={<GroupWorkIcon />} />
        <BottomNavigationAction label="Me" icon={<PersonIcon />} />
      </BottomNavigation>
    </Box>
  );
}
